import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert } from '@/components/ui/alert'
import { Spinner } from '@/components/ui/spinner'
import { Separator } from '@/components/ui/separator'

import {
  authenticateUser,
  requestLogin,
  reportUsername,
  reportPassword,
  reportNewPassword,
  reportRepeatPassword,
  reportVerificationCode,
  requestCloseOfResetPassword,
  requestPasswordReset,
  requestShowRequestForVerificationCode,
  requestCloseRequestForVerificationCode,
  requestSendVerificationCode,
  requestShowPasswordResetWithCode
} from '../../stores/auth'

import PasswordReset from '../../components/PasswordReset'
import SendVerificationCode from '../../components/SendVerificationCode'

import aberdeenLogo from '../../images/aber_hub_stacked-dark.svg'

import './index.css'

class Login extends Component {
  componentDidMount() {
    this.props.authenticateUser()
  }

  render() {
    const {
      login,
      passwordReset,
      verificationCode,
      username,
      requestLogin,
      reportUsername,
      reportPassword,
      reportNewPassword,
      reportRepeatPassword,
      reportVerificationCode,
      requestCloseOfResetPassword,
      requestPasswordReset,
      requestShowRequestForVerificationCode,
      requestCloseRequestForVerificationCode,
      requestSendVerificationCode,
      requestShowPasswordResetWithCode
    } = this.props

    return (
      <div className="Login flex min-h-screen items-center justify-center bg-secondary px-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="p-8 pt-8">
            <div className="mb-8 flex justify-center">
              <img src={aberdeenLogo} alt="Aberdeen Hub" width={280} />
            </div>

            <div className="flex flex-col gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="login-username">Username</Label>
                <Input
                  id="login-username"
                  type="text"
                  value={username}
                  placeholder="Your email address in all lowercase"
                  onChange={e => reportUsername(e.target.value)}
                  autoComplete="username"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="login-password">Password</Label>
                <Input
                  id="login-password"
                  type="password"
                  value={login.password}
                  onChange={e => reportPassword(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') requestLogin() }}
                  autoComplete="current-password"
                />
              </div>

              {login.hasError && <Alert variant="error">{login.error}</Alert>}

              {passwordReset.success && (
                <Alert variant="success">Password reset successful — please log in</Alert>
              )}

              <Button
                size="lg"
                className="mt-1 w-full"
                onClick={requestLogin}
                disabled={login.loading}
              >
                {login.loading && <Spinner size={18} className="text-primary-foreground" />}
                {login.loading ? 'Authenticating…' : 'Log In'}
              </Button>

              <Separator className="my-0.5" />

              <div className="flex items-center justify-center gap-1">
                <Button variant="link" size="sm" onClick={requestShowRequestForVerificationCode}>
                  Forgot Password?
                </Button>
                <span className="text-sm text-muted-foreground">·</span>
                <Button variant="link" size="sm" onClick={requestShowPasswordResetWithCode}>
                  Have a Reset Code?
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <PasswordReset
          show={passwordReset.show}
          hasError={passwordReset.hasError}
          error={passwordReset.error}
          loading={passwordReset.loading}
          username={username}
          verificationCode={passwordReset.verificationCode}
          newPassword={passwordReset.newPassword}
          repeatPassword={passwordReset.repeatPassword}
          requiresVerificationCode={passwordReset.resetWithCode}
          verificationCodeSent={verificationCode.success}
          reportUsername={reportUsername}
          reportNewPassword={reportNewPassword}
          reportRepeatPassword={reportRepeatPassword}
          reportVerificationCode={reportVerificationCode}
          requestClose={requestCloseOfResetPassword}
          requestPasswordReset={requestPasswordReset}
        />

        <SendVerificationCode
          show={verificationCode.show}
          requestClose={requestCloseRequestForVerificationCode}
          hasError={verificationCode.hasError}
          error={verificationCode.error}
          loading={verificationCode.loading}
          username={username}
          reportUsername={reportUsername}
          requestVerificationCode={requestSendVerificationCode}
        />
      </div>
    )
  }
}

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators({
    authenticateUser,
    requestLogin,
    reportUsername,
    reportPassword,
    reportNewPassword,
    reportRepeatPassword,
    reportVerificationCode,
    requestCloseOfResetPassword,
    requestPasswordReset,
    requestShowRequestForVerificationCode,
    requestCloseRequestForVerificationCode,
    requestSendVerificationCode,
    requestShowPasswordResetWithCode
  }, dispatch)
})

const mapStateToProps = (state, route) => {
  const { username, login, passwordReset, verificationCode } = state.stores.auth
  return {
    ...route.match.params,
    username,
    login,
    passwordReset,
    verificationCode
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Login))
